import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { SheetActionRow } from "../../../UIToolkit/Clickable/SheetActionRow";
import type { GapsRemovalState } from "./useGapsRemoval";

type Props = {
	removal: Pick<
		GapsRemovalState,
		| "shouldFindReplacementForShift"
		| "setShouldFindReplacementForShift"
		| "isReplacementGateReady"
		| "isReplacementGateError"
		| "replacementAdvice"
		| "isReplacementHintOpen"
	>;
	testIdPrefix: string;
};

/**
 * The line under the toggle: why it is off while the gate is pending or failed, then the call-off gate's advice
 * (worded for its HPPD/ratio basis) while it applies to the current toggle — the same rule as the desktop removal
 * dialog — otherwise what the toggle does.
 */
const replacementDescriptionKey = ({ isReplacementGateReady, isReplacementGateError, replacementAdvice, isReplacementHintOpen }: Props["removal"]): string => {
	if (isReplacementGateError) return "gaps.callIn.removal.findReplacementUnavailable";
	if (!isReplacementGateReady) return "gaps.callIn.removal.findReplacementChecking";
	if (isReplacementHintOpen && replacementAdvice?.message) return replacementAdvice.message.messageKey;
	return "gaps.callIn.removal.findReplacementHint";
};

/** "Find a replacement": the call-off agent's toggle, off until its gate is ready. */
export const GapsRemovalReplacementRow: FC<Props> = ({ removal, testIdPrefix }) => {
	const { t } = useTranslation();
	return (
		<Box data-testid={`${testIdPrefix}-replacement`}>
			<SheetActionRow
				kind="switch"
				checked={removal.shouldFindReplacementForShift}
				disabled={!removal.isReplacementGateReady}
				onClick={() => removal.setShouldFindReplacementForShift(!removal.shouldFindReplacementForShift)}
				icon={<PersonSearchOutlinedIcon />}
				label={t("gaps.callIn.removal.findReplacement")}
				description={t(replacementDescriptionKey(removal))}
				dataTestId={`${testIdPrefix}-replacement-toggle`}
			/>
		</Box>
	);
};
